import React, {useEffect, useState} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {Button, CircularProgress, Dialog, DialogContent, IconButton, Tooltip, Typography} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import GetAppIcon from '@material-ui/icons/GetApp';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import RotateLeftIcon from '@material-ui/icons/RotateLeft';
import RotateRightIcon from '@material-ui/icons/RotateRight';
import Papa from 'papaparse';

const IMAGE_EXT = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'];
const OFFICE_EXT = ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'];
const TEXT_EXT = ['txt', 'csv', 'log', 'json', 'xml', 'md'];
const OFFICE_VIEWER = 'https://view.officeapps.live.com/op/view.aspx?src=';
const MIN_ZOOM = 1;
const MAX_ZOOM = 5;
const PAN_STEP = 50;
const CSV_MAX_ROWS = 500;

const fileExtOf = (row) => {
    const name = String((row && row.file_name) || '');
    const idx = name.lastIndexOf('.');
    return idx >= 0 ? name.substring(idx + 1).toLowerCase() : '';
};

export const getPreviewKind = (row) => {
    const ext = fileExtOf(row);
    const type = String((row && row.content_type) || '').toLowerCase();
    if (IMAGE_EXT.includes(ext) || type.indexOf('image/') === 0) return 'image';
    if (ext === 'pdf' || type === 'application/pdf') return 'pdf';
    if (OFFICE_EXT.includes(ext)) return 'office';
    if (TEXT_EXT.includes(ext) || type === 'text/plain' || type === 'text/csv') return 'text';
    return null;
};

const useStyles = makeStyles((theme) => {
    const dark = theme.palette.type === 'dark';
    return {
        paper: {
            height: '85vh',
            maxHeight: '85vh',
        },
        content: {
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            padding: 0,
            overflow: 'hidden',
            position: 'relative',
            '&:first-child': {
                paddingTop: 0,
            },
        },
        topBar: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(0.5),
            padding: theme.spacing(1, 1, 1, 2),
            background: dark ? '#2b2b2b' : '#ffffff',
            borderBottom: `1px solid ${dark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
        },
        topInfo: {
            flex: 1,
            minWidth: 0,
        },
        fileName: {
            fontSize: 14,
            fontWeight: 600,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
        },
        fileMeta: {
            fontSize: 11,
            color: dark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
        },
        stage: {
            flex: 1,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            overflow: 'hidden',
            backgroundColor: '#000',
            position: 'relative',
        },
        frameWrap: {
            flex: 1,
            display: 'flex',
            overflow: 'hidden',
            backgroundColor: dark ? '#1e1e1e' : '#e0e0e0',
            position: 'relative',
        },
        frame: {
            flex: 1,
            width: '100%',
            height: '100%',
            border: 0,
            backgroundColor: '#ffffff',
        },
        textWrap: {
            flex: 1,
            overflow: 'auto',
            padding: theme.spacing(2),
            backgroundColor: dark ? '#1e1e1e' : '#ffffff',
            color: dark ? 'rgba(255,255,255,0.88)' : 'rgba(0,0,0,0.85)',
            '&::-webkit-scrollbar': {
                width: 6,
                height: 6,
            },
            '&::-webkit-scrollbar-track': {
                background: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
                background: dark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)',
                borderRadius: 3,
                transition: 'background 0.3s ease',
            },
            '&:hover::-webkit-scrollbar-thumb': {
                background: dark ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.2)',
            },
            '&::-webkit-scrollbar-thumb:hover': {
                background: dark ? 'rgba(255, 255, 255, 0.4)' : 'rgba(0, 0, 0, 0.4)',
            },
            scrollbarWidth: 'thin',
            scrollbarColor: (dark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)') + ' transparent',
        },
        textPre: {
            margin: 0,
            fontFamily: 'Consolas, "Courier New", monospace',
            fontSize: 13,
            lineHeight: 1.5,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
        },
        csvTable: {
            borderCollapse: 'collapse',
            fontSize: 13,
            '& th, & td': {
                border: `1px solid ${dark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)'}`,
                padding: '4px 10px',
                textAlign: 'left',
                whiteSpace: 'nowrap',
            },
            '& th': {
                position: 'sticky',
                top: 0,
                backgroundColor: dark ? '#2b2b2b' : '#f5f5f5',
                fontWeight: 700,
            },
        },
        textStatus: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(1),
            padding: theme.spacing(1, 0),
            fontSize: 12,
            color: dark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
        },
        textError: {
            color: '#ff8a80',
            fontSize: 13,
        },
        image: {
            maxWidth: 800,
            maxHeight: '100%',
            width: 'auto',
            height: 'auto',
            objectFit: 'contain',
            transformOrigin: 'center center',
            userSelect: 'none',
            transform: (p) => `translate(${p.x}px, ${p.y}px) rotate(${p.rotation}deg) scale(${p.zoom})`,
            transition: (p) => (p.dragging ? 'none' : 'transform 0.2s ease-out'),
            cursor: (p) => (p.zoom > 1 ? (p.dragging ? 'grabbing' : 'grab') : 'default'),
        },
        hint: {
            position: 'absolute',
            top: 20,
            left: 20,
            backgroundColor: 'rgba(0,0,0,0.7)',
            color: '#ffffff',
            padding: '8px 12px',
            borderRadius: 6,
            fontSize: 14,
            fontWeight: 500,
            pointerEvents: 'none',
            opacity: 0.8,
            zIndex: 10,
        },
        navWrap: {
            position: 'absolute',
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 10,
            display: 'inline-flex',
        },
        navBtn: {
            backgroundColor: 'rgba(0,0,0,0.5)',
            color: '#ffffff',
            width: 44,
            height: 44,
            padding: 0,
            '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.7)',
            },
            '&.Mui-disabled': {
                backgroundColor: 'rgba(0,0,0,0.2)',
                color: 'rgba(255,255,255,0.3)',
            },
        },
        navPrev: {
            left: 16,
        },
        navNext: {
            right: 16,
        },
        counter: {
            position: 'absolute',
            top: 16,
            right: 16,
            backgroundColor: 'rgba(0,0,0,0.6)',
            color: '#ffffff',
            padding: '4px 12px',
            borderRadius: 20,
            fontSize: 13,
            fontWeight: 600,
            zIndex: 10,
            letterSpacing: '0.5px',
        },
        controlBar: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexWrap: 'wrap',
            padding: '14px 24px',
            backgroundColor: '#FEF200',
            gap: 12,
            boxShadow: '0 -4px 20px rgba(0,0,0,0.08)',
        },
        group: {
            display: 'flex',
            alignItems: 'center',
            backgroundColor: 'rgba(255,255,255,0.95)',
            borderRadius: 12,
            padding: '6px 8px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
            gap: 2,
        },
        ctrlBtn: {
            backgroundColor: '#f0f0f0',
            width: 34,
            height: 34,
            borderRadius: 8,
            color: '#1a1a1a',
            transition: 'all 0.15s ease',
            '& svg': {
                fontSize: 17,
            },
            '&.Mui-disabled': {
                backgroundColor: 'transparent',
                color: '#aaaaaa',
            },
        },
        ctrlSign: {
            fontSize: 18,
            fontWeight: 600,
            lineHeight: 1,
            color: 'inherit',
        },
        ctrlValue: {
            minWidth: 56,
            padding: '4px 6px',
            textAlign: 'center',
            fontWeight: 700,
            color: '#1a1a1a',
            fontSize: 13,
            letterSpacing: '-0.3px',
        },
        padGrid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 30px)',
            gap: 2,
        },
        padBtn: {
            backgroundColor: '#f0f0f0',
            width: 30,
            height: 30,
            borderRadius: 6,
            padding: 0,
            color: '#1a1a1a',
            transition: 'all 0.15s ease',
            '& svg': {
                fontSize: 16,
            },
            '&.Mui-disabled': {
                backgroundColor: 'transparent',
                color: '#aaaaaa',
            },
        },
        resetBtn: {
            padding: '8px 18px',
            borderRadius: 10,
            backgroundColor: 'rgba(26,26,26,0.9)',
            color: '#ffffff',
            fontWeight: 600,
            fontSize: 13,
            textTransform: 'none',
            boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
            '&:hover': {
                backgroundColor: '#000000',
            },
            '&.Mui-disabled': {
                backgroundColor: 'rgba(255,255,255,0.6)',
                color: '#999999',
                boxShadow: 'none',
            },
        },
        saveBtn: {
            padding: '8px 18px',
            borderRadius: 10,
            backgroundColor: 'rgba(102,187,106,0.9)',
            color: '#ffffff',
            fontWeight: 600,
            fontSize: 13,
            textTransform: 'none',
            boxShadow: '0 2px 8px rgba(102,187,106,0.25)',
            '&:hover': {
                backgroundColor: '#43a047',
            },
            '&.Mui-disabled': {
                backgroundColor: 'rgba(255,255,255,0.6)',
                color: '#999999',
                boxShadow: 'none',
            },
        },
    };
});

function TextPreview({item, classes, language, onLoadText}) {
    const [content, setContent] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const fileUrl = item ? item.file_url : '';
    const isCsv = fileExtOf(item) === 'csv';

    useEffect(() => {
        if (!item || typeof onLoadText !== 'function') return undefined;
        let active = true;
        setLoading(true);
        setError('');
        setContent('');
        onLoadText(item).then((text) => {
            if (!active) return;
            setContent(text || '');
            setLoading(false);
        }).catch((e) => {
            if (!active) return;
            setError((e && e.message) || language.preview_failed);
            setLoading(false);
        });
        return () => { active = false; };
    }, [fileUrl]);

    if (loading) {
        return (
            <div className={classes.textWrap}>
                <div className={classes.textStatus}>
                    <CircularProgress size={16} color="inherit"/>
                    {language.loading}
                </div>
            </div>
        );
    }
    if (error !== '') {
        return (
            <div className={classes.textWrap}>
                <div className={classes.textError}>{error}</div>
            </div>
        );
    }
    if (isCsv) {
        const parsed = Papa.parse(content, {skipEmptyLines: true});
        const rows = Array.isArray(parsed.data) ? parsed.data : [];
        const header = rows.length > 0 ? rows[0] : [];
        const body = rows.slice(1, CSV_MAX_ROWS + 1);
        const truncated = rows.length - 1 > CSV_MAX_ROWS;
        return (
            <div className={classes.textWrap}>
                <table className={classes.csvTable}>
                    <thead>
                        <tr>
                            {header.map((cell, i) => <th key={'h' + i}>{cell}</th>)}
                        </tr>
                    </thead>
                    <tbody>
                        {
                            body.map((row, r) => (
                                <tr key={'r' + r}>
                                    {row.map((cell, c) => <td key={'c' + c}>{cell}</td>)}
                                </tr>
                            ))
                        }
                    </tbody>
                </table>
                {truncated && <div className={classes.textStatus}>{`${language.showing_first_rows} ${CSV_MAX_ROWS}`}</div>}
            </div>
        );
    }
    return (
        <div className={classes.textWrap}>
            <pre className={classes.textPre}>{content}</pre>
        </div>
    );
}

export default function AttachmentViewer({
    open, items, index, language, onClose, onNavigate, onDownload, downloading, onLoadText, onSaveRotation, savingRotation
}) {
    const list = Array.isArray(items) ? items : [];
    const item = index >= 0 && index < list.length ? list[index] : null;
    const kind = item ? getPreviewKind(item) : null;
    const fileUrl = item ? item.file_url : '';
    const [zoom, setZoom] = useState(MIN_ZOOM);
    const [position, setPosition] = useState({x: 0, y: 0});
    const [rotation, setRotation] = useState(0);
    const [dragging, setDragging] = useState(false);
    const [dragStart, setDragStart] = useState({x: 0, y: 0});
    const [dragStartPos, setDragStartPos] = useState({x: 0, y: 0});
    const classes = useStyles({x: position.x, y: position.y, rotation, zoom, dragging});

    const resetView = () => {
        setZoom(MIN_ZOOM);
        setPosition({x: 0, y: 0});
        setRotation(0);
        setDragging(false);
    };

    useEffect(() => {
        resetView();
    }, [fileUrl, open]);

    const handleMouseDown = (e) => {
        if (zoom > 1) {
            setDragging(true);
            setDragStart({x: e.clientX, y: e.clientY});
            setDragStartPos({x: position.x, y: position.y});
            e.preventDefault();
        }
    };

    useEffect(() => {
        if (!dragging) return undefined;
        const onMove = (e) => {
            setPosition({
                x: dragStartPos.x + (e.clientX - dragStart.x),
                y: dragStartPos.y + (e.clientY - dragStart.y),
            });
        };
        const onUp = () => setDragging(false);
        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
        return () => {
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onUp);
        };
    }, [dragging, dragStart, dragStartPos]);

    const canPrev = index > 0;
    const canNext = index < list.length - 1;
    const goPrev = () => { if (canPrev) onNavigate(index - 1); };
    const goNext = () => { if (canNext) onNavigate(index + 1); };

    const zoomOut = () => {
        const next = Math.max(MIN_ZOOM, Math.round((zoom - 0.1) * 10) / 10);
        setZoom(next);
        if (next === MIN_ZOOM) setPosition({x: 0, y: 0});
    };
    const zoomIn = () => setZoom(Math.min(MAX_ZOOM, Math.round((zoom + 0.1) * 10) / 10));
    const panUp = () => setPosition({x: position.x, y: position.y + PAN_STEP});
    const panDown = () => setPosition({x: position.x, y: position.y - PAN_STEP});
    const panLeft = () => setPosition({x: position.x + PAN_STEP, y: position.y});
    const panRight = () => setPosition({x: position.x - PAN_STEP, y: position.y});
    const rotateLeft = () => setRotation((rotation - 90) % 360);
    const rotateRight = () => setRotation((rotation + 90) % 360);
    const normalizedRotation = ((rotation % 360) + 360) % 360;
    const isDirty = zoom !== MIN_ZOOM || position.x !== 0 || position.y !== 0 || rotation !== 0;
    const canSave = normalizedRotation !== 0 && !savingRotation;

    const handleSave = () => { if (item && canSave) onSaveRotation(item, normalizedRotation); };
    const handleDownload = () => { if (item) onDownload(item); };
    const openNewTab = () => { if (item) window.open(item.file_url, '_blank'); };

    const frameSrc = kind === 'office' ? OFFICE_VIEWER + encodeURIComponent(fileUrl) : fileUrl;
    const showNav = list.length > 1;

    return (
        <Dialog fullWidth maxWidth="lg" open={open} onClose={onClose} classes={{paper: classes.paper}}>
            <DialogContent className={classes.content}>
                <div className={classes.topBar}>
                    <div className={classes.topInfo}>
                        <Typography className={classes.fileName} title={item ? item.file_name : ''}>
                            {item ? item.file_name : ''}
                        </Typography>
                        <Typography className={classes.fileMeta}>
                            {item && item.remark ? item.remark : ''}
                        </Typography>
                    </div>
                    <Tooltip title={language.download}>
                        <span>
                            <IconButton onClick={handleDownload} disabled={downloading}>
                                {downloading ? <CircularProgress size={18} color="inherit"/> : <GetAppIcon/>}
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Tooltip title={language.open_in_new_tab}>
                        <IconButton onClick={openNewTab}>
                            <OpenInNewIcon/>
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={language.close}>
                        <IconButton onClick={onClose}>
                            <CloseIcon/>
                        </IconButton>
                    </Tooltip>
                </div>
                <div className={kind === 'image' ? classes.stage : classes.frameWrap}>
                    {
                        kind === 'image' && (
                            <img
                                src={fileUrl}
                                alt={item ? item.file_name : ''}
                                onMouseDown={handleMouseDown}
                                draggable={false}
                                className={classes.image}/>
                        )
                    }
                    {
                        kind === 'image' && zoom > 1 && !dragging && (
                            <div className={classes.hint}>{language.drag_to_move_hint}</div>
                        )
                    }
                    {
                        (kind === 'pdf' || kind === 'office') && (
                            <iframe title={item ? item.file_name : 'preview'} src={frameSrc} className={classes.frame}/>
                        )
                    }
                    {
                        kind === 'text' && (
                            <TextPreview item={item} classes={classes} language={language} onLoadText={onLoadText}/>
                        )
                    }
                    {
                        showNav && (
                            <>
                                <Tooltip title={language.previous_file}>
                                    <span className={`${classes.navWrap} ${classes.navPrev}`}>
                                        <IconButton onClick={goPrev} disabled={!canPrev} className={classes.navBtn}>
                                            <ArrowBackIcon/>
                                        </IconButton>
                                    </span>
                                </Tooltip>
                                <Tooltip title={language.next_file}>
                                    <span className={`${classes.navWrap} ${classes.navNext}`}>
                                        <IconButton onClick={goNext} disabled={!canNext} className={classes.navBtn}>
                                            <ArrowForwardIcon/>
                                        </IconButton>
                                    </span>
                                </Tooltip>
                                <div className={classes.counter}>{`${index + 1} / ${list.length}`}</div>
                            </>
                        )
                    }
                </div>
                {
                    kind === 'image' && (
                        <div className={classes.controlBar}>
                            <div className={classes.group}>
                                <Tooltip title={language.zoom_out}>
                                    <span>
                                        <IconButton onClick={zoomOut} disabled={zoom <= MIN_ZOOM} className={classes.ctrlBtn}>
                                            <Typography className={classes.ctrlSign}>-</Typography>
                                        </IconButton>
                                    </span>
                                </Tooltip>
                                <Typography className={classes.ctrlValue}>{`${Math.round(zoom * 100)}%`}</Typography>
                                <Tooltip title={language.zoom_in}>
                                    <span>
                                        <IconButton onClick={zoomIn} disabled={zoom >= MAX_ZOOM} className={classes.ctrlBtn}>
                                            <Typography className={classes.ctrlSign}>+</Typography>
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            </div>
                            <div className={classes.group}>
                                <div className={classes.padGrid}>
                                    <div/>
                                    <IconButton onClick={panUp} disabled={zoom <= MIN_ZOOM} className={classes.padBtn}>
                                        <ArrowUpwardIcon/>
                                    </IconButton>
                                    <div/>
                                    <IconButton onClick={panLeft} disabled={zoom <= MIN_ZOOM} className={classes.padBtn}>
                                        <ArrowBackIcon/>
                                    </IconButton>
                                    <IconButton onClick={panDown} disabled={zoom <= MIN_ZOOM} className={classes.padBtn}>
                                        <ArrowDownwardIcon/>
                                    </IconButton>
                                    <IconButton onClick={panRight} disabled={zoom <= MIN_ZOOM} className={classes.padBtn}>
                                        <ArrowForwardIcon/>
                                    </IconButton>
                                </div>
                            </div>
                            <div className={classes.group}>
                                <Tooltip title={language.rotate_left}>
                                    <IconButton onClick={rotateLeft} className={classes.ctrlBtn}>
                                        <RotateLeftIcon/>
                                    </IconButton>
                                </Tooltip>
                                <Typography className={classes.ctrlValue}>{`${normalizedRotation}°`}</Typography>
                                <Tooltip title={language.rotate_right}>
                                    <IconButton onClick={rotateRight} className={classes.ctrlBtn}>
                                        <RotateRightIcon/>
                                    </IconButton>
                                </Tooltip>
                            </div>
                            <Button onClick={resetView} disabled={!isDirty} className={classes.resetBtn}>
                                {language.reset}
                            </Button>
                            <Button
                                onClick={handleSave}
                                disabled={!canSave}
                                className={classes.saveBtn}
                                startIcon={savingRotation ? <CircularProgress size={14} color="inherit"/> : null}>
                                {language.save_rotation}
                            </Button>
                        </div>
                    )
                }
            </DialogContent>
        </Dialog>
    );
}
